import type {ShipmentEntryInputData} from "./ShipmentEntryInputGroup";
import {InitialShipmentEntryInputData} from "./ShipmentEntryInputGroup";

import type {FieldErrors} from "./types";

export interface ShipmentDetail {
  id: string;
  status: STATUS_ENUM;
  fieldErrors: FieldErrors;
  apiError?: string;
  calculatedEmissions?: number;
  data: ShipmentEntryInputData;
}

interface State {
  [key: string]: ShipmentDetail;
}

interface UpdateShipmentDetail {
  type: "UpdateShipmentDetail";
  payload: {
    id: string;
    status: STATUS_ENUM;
    fieldErrors: FieldErrors;
    apiError?: string;
    calculatedEmissions?: number;
    data: ShipmentEntryInputData;
  };
}

interface AddShipmentDetail {
  type: "AddShipmentDetail";
  payload: {
    id: string;
    status: STATUS_ENUM;
    fieldErrors: FieldErrors;
  };
}

interface RemoveShipmentDetail {
  type: "RemoveShipmentDetail";
  payload: {
    id: string;
  };
}

interface UpdateShipmentStatus {
  type: "UpdateShipmentStatus";
  payload: {
    id: string;
    status: STATUS_ENUM;
  };
}

export const ActionEnum = Object.freeze({
  UpdateShipmentDetail: "UpdateShipmentDetail",
  AddShipmentDetail: "AddShipmentDetail",
  RemoveShipmentDetail: "RemoveShipmentDetail",
  UpdateShipmentStatus: "UpdateShipmentStatus",
});

type Actions =
  | UpdateShipmentDetail
  | AddShipmentDetail
  | RemoveShipmentDetail
  | UpdateShipmentStatus;

export const initialFieldErrors: FieldErrors = {
  transportationMode: "",
  originPort: "",
  destinationPort: "",
  originAddress: "",
  destinationAddress: "",
  weight: "",
  teus: "",
};

export enum STATUS_ENUM {
  NEW = "new",
  CALCULATING = "calculating...",
  SUCCESS = "success",
  ERROR = "error",
  API_ERROR = "api_error",
}

export const InitialEmissionsCalculatorState: State = {
  "0": {
    id: "0",
    status: STATUS_ENUM.NEW,
    fieldErrors: initialFieldErrors,
    calculatedEmissions: undefined,
    data: InitialShipmentEntryInputData,
  },
};

export function EmissionsCalculatorReducer(
  state: State,
  action: Actions
): State {
  switch (action.type) {
    case ActionEnum.UpdateShipmentDetail: {
      const {id, data, calculatedEmissions, status, fieldErrors, apiError} =
        action.payload;

      const shipmentDetail = state[id];
      return {
        ...state,
        [id]: {
          ...shipmentDetail,
          calculatedEmissions,
          status,
          fieldErrors,
          apiError: apiError ?? undefined,
          data: {
            ...shipmentDetail.data,
            ...data,
          },
        },
      };
    }
    case ActionEnum.AddShipmentDetail: {
      const payload = action.payload;
      return {
        ...state,
        [payload.id]: {
          id: payload.id,
          calculatedEmissions: undefined,
          data: InitialShipmentEntryInputData,
          status: payload.status,
          fieldErrors: payload.fieldErrors,
        },
      };
    }
    case ActionEnum.RemoveShipmentDetail: {
      const {id} = action.payload;
      const newState = {...state};
      delete newState[id];
      return newState;
    }
    case ActionEnum.UpdateShipmentStatus: {
      const {id, status} = action.payload;
      return {
        ...state,
        [id]: {
          ...state[id],
          status,
        },
      };
    }
    default:
      return state;
  }
}
